import { apiRequest } from './client';
import { User } from '../types';

export interface PayoutInfoInput {
  dataConsent: boolean;
  identityIdNumber?: string;
  payoutBankName?: string;
  payoutAccountNumber?: string;
  payoutAccountHolderName?: string;
  deviceFingerprint?: string;
}

export function getMe(token: string) {
  return apiRequest<User>('/users/me', { token });
}

export interface ReferralProgressGroup {
  count: number;
  cap: number;
  rewardCoins: number;
  full: boolean;
}

export interface ReferralProgress {
  shopOwner: ReferralProgressGroup;
  reviewer: ReferralProgressGroup;
}

export function getReferralProgress(token: string) {
  return apiRequest<ReferralProgress>('/users/referral-progress', { token });
}

export function markOnboardingSeen(token: string) {
  return apiRequest<{ onboardingSeenAt: boolean }>('/users/me/onboarding-seen', { method: 'POST', token });
}

export function updatePayoutInfo(token: string, input: PayoutInfoInput) {
  return apiRequest<User>('/users/me/payout-info', { method: 'PATCH', token, body: input });
}

export type VipPlan = 'MONTHLY' | 'YEARLY' | 'LIFETIME';

export function purchaseVip(token: string, plan: VipPlan) {
  return apiRequest<User>('/users/me/vip', { method: 'POST', token, body: { plan } });
}

/** photoUri is a local file:// (or web blob:) URI from the camera capture. */
export function uploadIdentityPhoto(token: string, photoUri: string) {
  const form = new FormData();
  const filename = photoUri.split('/').pop() ?? `identity-${Date.now()}.jpg`;
  const ext = filename.split('.').pop()?.toLowerCase();
  const mime = ext === 'png' ? 'image/png' : 'image/jpeg';
  form.append('photo', { uri: photoUri, name: filename, type: mime } as unknown as Blob);
  return apiRequest<User>('/users/me/identity-photo', { method: 'POST', token, form });
}

/** photoUri is a local file:// (or web blob:) URI from the image picker. */
export function uploadAvatar(token: string, photoUri: string) {
  const form = new FormData();
  const filename = photoUri.split('/').pop() ?? `avatar-${Date.now()}.jpg`;
  const ext = filename.split('.').pop()?.toLowerCase();
  const mime = ext === 'png' ? 'image/png' : 'image/jpeg';
  form.append('photo', { uri: photoUri, name: filename, type: mime } as unknown as Blob);
  return apiRequest<User>('/users/me/avatar', { method: 'POST', token, form });
}

export function removeAvatar(token: string) {
  return apiRequest<User>('/users/me/avatar', { method: 'DELETE', token });
}

/** Short-lived (5 min), single-purpose token for loading the identity photo
 * as an <Image> src — NOT the session token. See backend JwtPhotoStrategy. */
export function getIdentityPhotoToken(token: string) {
  return apiRequest<{ token: string }>('/users/me/identity-photo-token', { token });
}

/** devOtpCode is only ever present when no real SMS provider is configured
 * (dev/test) — never sent in production. */
export function requestPhoneOtp(token: string, phone: string) {
  return apiRequest<{ devOtpCode?: string } | void>('/users/me/phone/request-otp', {
    method: 'POST',
    token,
    body: { phone },
  });
}

export function verifyPhoneOtp(token: string, code: string) {
  return apiRequest<User>('/users/me/phone/verify-otp', { method: 'POST', token, body: { code } });
}

/** devOtpCode is only ever present when no real SMTP is configured (dev/test). */
export function requestEmailOtp(token: string) {
  return apiRequest<{ devOtpCode?: string } | void>('/users/me/email/request-otp', { method: 'POST', token });
}

export function verifyEmailOtp(token: string, code: string) {
  return apiRequest<User>('/users/me/email/verify-otp', { method: 'POST', token, body: { code } });
}

export function deleteOwnAccount(token: string, password: string) {
  return apiRequest<{ success: boolean }>('/users/me/delete-account', {
    method: 'POST',
    token,
    body: { password },
  });
}
